/**
 * @internal
 * Empty state rendered by LineChart when data is absent. Not part of the public API.
 * Use `LineChart` instead — it handles loading, empty, and rendered states automatically.
 */
import React, { forwardRef, RefObject, useEffect, useRef } from 'react';
import { scaleLinear as d3scaleLinear } from 'd3';

import { AnyScale, JSONArray } from '../../types';
import { LineChartProps } from './types';
import { StyledLineChart, StyledLineChartEmptyText } from './styles';
import { Axis } from '../Axis';
import { sizeToDimension } from './utils';
import { useTheme } from '@redsift/design-system';

export const EmptyLineChart = forwardRef<HTMLDivElement, LineChartProps>((props, ref) => {
  const { className, data: propsData, emptyComponent, localeText, size, ...forwardedProps } = props;
  const theme = useTheme();

  const cache = useRef<JSONArray>();

  const data = propsData;

  useEffect(() => {
    cache.current = data;
  });

  const chartDimensions = sizeToDimension(size!);
  const width = chartDimensions.width;
  const height = chartDimensions.height;
  const margins = { top: 16, left: 48, right: 16, bottom: 48 };
  const chartWidth = width - margins.left - margins.right;

  return (
    <StyledLineChart {...forwardedProps} className={className} ref={ref as RefObject<HTMLDivElement>}>
      {emptyComponent ?? (
        <>
          <StyledLineChartEmptyText $maxWidth={width} $textSize={chartDimensions.fontSize / 2} $theme={theme}>
            <span>{localeText?.emptyChartText}</span>
          </StyledLineChartEmptyText>
          <svg width={width} height={height}>
            <g transform={`translate(${margins.left},${margins.top})`}>
              <Axis
                position="bottom"
                length={width - 32}
                scale={d3scaleLinear().domain([]).range([0, chartWidth]) as AnyScale}
                x={0}
                y={height}
                tickValues={4}
              />
            </g>
          </svg>
        </>
      )}
    </StyledLineChart>
  );
});
